import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // ChunkLoadError happens when a code-split chunk fails to load — typically
    // because the dev server / CDN recompiled and the chunk hash referenced by
    // the cached HTML no longer exists. Recovery is always: reload the page.
    // We do it ONCE automatically (with a session flag) so users don't see the
    // generic error screen on a transient cache mismatch.
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [^ ]+ failed/i.test(error?.message || '') ||
      /Loading CSS chunk [^ ]+ failed/i.test(error?.message || '');
    if (isChunkError) {
      try {
        const alreadyReloaded = sessionStorage.getItem('__chunk_reload_attempted');
        if (!alreadyReloaded) {
          sessionStorage.setItem('__chunk_reload_attempted', String(Date.now()));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    } else {
      // Clear the chunk-reload flag on any non-chunk error so it can fire next time
      try { sessionStorage.removeItem('__chunk_reload_attempted'); } catch { /* noop */ }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Opcional: recargar la página o navegar a otra ruta
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-red-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Algo salió mal
            </h2>
            <p className="text-slate-600 mb-4">
              Ha ocurrido un error inesperado. Por favor, intenta recargar la página.
            </p>
            <div className="flex gap-2 justify-center">
              <Button 
                onClick={() => window.location.reload()}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Recargar página
              </Button>
              <Button 
                variant="outline"
                onClick={this.handleReset}
              >
                Intentar de nuevo
              </Button>
            </div>
            {this.state.error && (
              <details className="mt-4 text-left text-xs text-red-600 bg-red-50 p-3 rounded">
                <summary className="cursor-pointer font-medium">Detalles del error</summary>
                <pre className="mt-2 whitespace-pre-wrap overflow-auto max-h-48">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
